/**
 * 手帳（Tab キー）。
 *
 * タブ構成：目標 / 手がかり / 原因メモ（仮説） / 知識カード / 地図
 */

import type { GlossaryEntry } from '../scenario/dataSource';
import type { ScenarioEngine } from '../scenario/engine';
import type { OfficeLayout } from '../engine/layout';
import type { RoomId } from '../types/network';
import { button, clear, el } from './dom';
import addressPlan from '../data/address-plan.json';

type TabId = 'objectives' | 'clues' | 'hypotheses' | 'cards' | 'map';

const TAB_LABELS: { id: TabId; label: string }[] = [
  { id: 'objectives', label: '目標' },
  { id: 'clues', label: '手がかり' },
  { id: 'hypotheses', label: '原因メモ' },
  { id: 'cards', label: '知識カード' },
  { id: 'map', label: '地図' },
];

export interface NotebookHooks {
  onChooseHypothesis: (hypothesisId: string) => void;
  onRequestHint: () => void;
  onClose: () => void;
  onTermClick: (entry: GlossaryEntry | null, term: string) => void;
}

export class Notebook {
  readonly root: HTMLElement;
  private tabBar: HTMLElement;
  private body: HTMLElement;
  private activeTab: TabId = 'objectives';
  private layout: OfficeLayout | null = null;
  private playerRoom: RoomId | null = null;
  private focusCardId: string | null = null;

  constructor(
    private readonly engine: ScenarioEngine,
    private readonly hooks: NotebookHooks,
  ) {
    this.tabBar = el('div', { class: 'nb-tabs' });
    this.body = el('div', { class: 'nb-body' });

    const panel = el('div', { class: 'nb-panel' }, [
      el('div', { class: 'nb-head' }, [
        el('span', { class: 'nb-title', text: '手帳' }),
        button('閉じる (Tab)', () => this.hooks.onClose(), 'btn btn-ghost btn-small'),
      ]),
      this.tabBar,
      this.body,
    ]);
    this.root = el('div', { class: 'nb-layer', hidden: true }, [panel]);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  setLayout(layout: OfficeLayout): void {
    this.layout = layout;
  }

  setPlayerRoom(roomId: RoomId | null): void {
    this.playerRoom = roomId;
    if (this.isOpen && this.activeTab === 'map') this.render();
  }

  open(tab: TabId = this.activeTab): void {
    this.activeTab = tab;
    this.root.hidden = false;
    this.render();
  }

  /** 知識カードを名指しで開く（用語ポップアップからの遷移） */
  openCard(cardId: string): void {
    this.focusCardId = cardId;
    this.open('cards');
  }

  close(): void {
    this.root.hidden = true;
    this.focusCardId = null;
  }

  render(): void {
    this.renderTabs();
    clear(this.body);
    switch (this.activeTab) {
      case 'objectives':
        this.renderObjectives();
        break;
      case 'clues':
        this.renderClues();
        break;
      case 'hypotheses':
        this.renderHypotheses();
        break;
      case 'cards':
        this.renderCards();
        break;
      case 'map':
        this.renderMap();
        break;
    }
  }

  private renderTabs(): void {
    clear(this.tabBar);
    for (const tab of TAB_LABELS) {
      const node = button(
        tab.label,
        () => {
          this.activeTab = tab.id;
          this.render();
        },
        this.activeTab === tab.id ? 'nb-tab active' : 'nb-tab',
      );
      this.tabBar.append(node);
    }
  }

  private renderObjectives(): void {
    const chapter = this.engine.chapter;
    if (!chapter) {
      this.body.append(el('p', { class: 'nb-empty', text: '進行中の章がありません。' }));
      return;
    }
    this.body.append(
      el('h3', { class: 'nb-h', text: `第${chapter.number}章\u3000${chapter.title}` }),
    );
    const list = el('ul', { class: 'nb-list' });
    for (const item of this.engine.getObjectives()) {
      if (!item.visible) {
        list.append(el('li', { class: 'nb-item muted', text: '（まだ見えていない目標）' }));
        continue;
      }
      list.append(
        el('li', { class: item.done ? 'nb-item done' : 'nb-item' }, [
          el('span', { class: 'nb-mark', text: item.done ? '✓' : '□' }),
          el('span', { text: item.objective.text }),
        ]),
      );
    }
    this.body.append(list);

    const hint = this.engine.getHintState();
    const hintBox = el('div', { class: 'nb-hintbox' });
    hintBox.append(el('h4', { class: 'nb-h4', text: 'ヘルプデスクAI「ポート」' }));
    if (!hint.available) {
      hintBox.append(el('p', { class: 'nb-empty', text: 'いまはヒントの用意がありません。' }));
    } else {
      if (hint.texts.length === 0) {
        hintBox.append(
          el('p', {
            class: 'nb-empty',
            text: '詰まったら聞いてください。段階的にヒントを出します（減点はありません）。',
          }),
        );
      }
      for (const text of hint.texts) {
        hintBox.append(el('p', { class: 'nb-hint', text }));
      }
      if (hint.revealed < hint.totalLevels) {
        hintBox.append(
          button(
            hint.revealed === 0
              ? 'ヒントをもらう'
              : `もう少し詳しいヒント（${hint.revealed + 1}/${hint.totalLevels}）`,
            () => {
              this.hooks.onRequestHint();
              this.render();
            },
            'btn btn-small',
          ),
        );
      } else {
        hintBox.append(el('p', { class: 'nb-empty', text: 'ヒントはここまでです。' }));
      }
    }
    this.body.append(hintBox);
  }

  private renderClues(): void {
    const clues = this.engine.getClues();
    const total = this.engine.getAllChapterClues().length;
    this.body.append(
      el('h3', { class: 'nb-h', text: `手がかり（${clues.length} / ${total}）` }),
    );
    if (clues.length === 0) {
      this.body.append(
        el('p', {
          class: 'nb-empty',
          text: 'まだ手がかりがありません。現場に行き、人に話を聞き、機器を調べてみましょう。',
        }),
      );
      return;
    }
    for (const clue of clues) {
      this.body.append(
        el('div', { class: 'nb-card' }, [
          el('div', { class: 'nb-card-title', text: clue.title }),
          el('div', { class: 'nb-card-body', text: clue.detail }),
        ]),
      );
    }
  }

  private renderHypotheses(): void {
    this.body.append(el('h3', { class: 'nb-h', text: '原因メモ' }));
    this.body.append(
      el('p', {
        class: 'nb-lead',
        text: '集めた手がかりから、原因の見当をつけましょう。外れても減点はありません。外れたときこそ学べます。',
      }),
    );

    const chapter = this.engine.chapter;
    if (!chapter || chapter.hypotheses.length === 0) {
      this.body.append(el('p', { class: 'nb-empty', text: 'この章には仮説がありません。' }));
      return;
    }

    const available = this.engine.getAvailableHypotheses();
    for (const hypothesis of chapter.hypotheses) {
      const unlocked = available.some((h) => h.id === hypothesis.id);
      const chosen = this.engine.isHypothesisChosen(hypothesis.id);
      const row = el('div', { class: 'nb-card' }, [
        el('div', { class: 'nb-card-body', text: unlocked ? hypothesis.text : '???（手がかりが足りません）' }),
      ]);
      if (unlocked) {
        row.append(
          chosen
            ? el('div', {
                class: hypothesis.correct ? 'nb-badge ok' : 'nb-badge ng',
                text: hypothesis.correct ? 'この線で対処した' : '検討済み（外れ）',
              })
            : button(
                'この仮説で進める',
                () => this.hooks.onChooseHypothesis(hypothesis.id),
                'btn btn-small',
              ),
        );
      }
      this.body.append(row);
    }

    const fixes = this.engine.getUnlockedFixes();
    if (fixes.length > 0) {
      this.body.append(el('h4', { class: 'nb-h4', text: '解放された対処' }));
      for (const fix of fixes) {
        const where = fix.requiresInteractable
          ? this.engine.getInteractable(fix.requiresInteractable)?.label ?? fix.requiresInteractable
          : 'どこでも';
        this.body.append(
          el('div', { class: 'nb-card' }, [
            el('div', { class: 'nb-card-title', text: fix.label }),
            el('div', { class: 'nb-card-body', text: `実行できる場所：${where}` }),
          ]),
        );
      }
    }
  }

  private renderCards(): void {
    const cards = this.engine.getKnowledgeCards();
    this.body.append(
      el('h3', { class: 'nb-h', text: `知識カード（${cards.length} 枚）` }),
    );
    if (cards.length === 0) {
      this.body.append(
        el('p', { class: 'nb-empty', text: 'まだ 1 枚もありません。調べたり教わったりすると増えます。' }),
      );
      return;
    }
    for (const card of cards) {
      const open = this.focusCardId === card.id;
      const detail = el('div', { class: 'nb-card-detail', hidden: !open });
      detail.append(
        el('p', { class: 'nb-card-line' }, [
          el('span', { class: 'term-tag', text: 'たとえるなら' }),
          document.createTextNode(card.analogy),
        ]),
        el('p', { class: 'nb-card-line' }, [
          el('span', { class: 'term-tag', text: 'ひとことで' }),
          document.createTextNode(card.summary),
        ]),
      );
      for (const paragraph of card.detail) {
        detail.append(el('p', { class: 'nb-card-para', text: paragraph }));
      }
      if (card.realWorld) {
        detail.append(
          el('p', { class: 'nb-card-real' }, [
            el('span', { class: 'term-tag', text: '実生活では' }),
            document.createTextNode(card.realWorld),
          ]),
        );
      }
      if (card.related?.length) {
        const related = card.related
          .map((id) => this.engine.getCard(id)?.term)
          .filter((t): t is string => !!t);
        if (related.length > 0) {
          detail.append(el('p', { class: 'nb-card-related', text: `関連：${related.join(' / ')}` }));
        }
      }

      const head = button(
        `${card.term}`,
        () => {
          detail.hidden = !detail.hidden;
        },
        'nb-card-toggle',
      );
      this.body.append(el('div', { class: 'nb-card' }, [head, detail]));
    }
    this.focusCardId = null;
  }

  /** 地図は 3D レイアウトから毎回描き起こす */
  private renderMap(): void {
    this.body.append(el('h3', { class: 'nb-h', text: '社内地図' }));
    const layout = this.layout;
    if (!layout) {
      this.body.append(el('p', { class: 'nb-empty', text: '地図をまだ描けません。' }));
      return;
    }

    for (const floor of [...layout.floorNumbers].sort((a, b) => b - a)) {
      const rooms = Object.values(layout.rooms).filter((r) => r.floor === floor);
      if (rooms.length === 0) continue;

      const minX = Math.min(...rooms.map((r) => r.center[0] - r.width / 2)) - 3;
      const maxX = Math.max(...rooms.map((r) => r.center[0] + r.width / 2)) + 3;
      const minZ = Math.min(...rooms.map((r) => r.center[2] - r.depth / 2)) - 3;
      const maxZ = Math.max(...rooms.map((r) => r.center[2] + r.depth / 2)) + 3;

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', `${minX} ${minZ} ${maxX - minX} ${maxZ - minZ}`);
      svg.setAttribute('class', 'nb-map');

      // 廊下
      const corridor = layout.walkRects.find((r) => r.id === `corridor:${floor}`);
      if (corridor) {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', String(corridor.x0));
        rect.setAttribute('y', String(corridor.z0));
        rect.setAttribute('width', String(corridor.x1 - corridor.x0));
        rect.setAttribute('height', String(corridor.z1 - corridor.z0));
        rect.setAttribute('class', 'nb-map-corridor');
        svg.append(rect);
      }

      for (const room of rooms) {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', String(room.center[0] - room.width / 2));
        rect.setAttribute('y', String(room.center[2] - room.depth / 2));
        rect.setAttribute('width', String(room.width));
        rect.setAttribute('height', String(room.depth));
        const locked =
          room.lockedUntilFlag && this.engine.state.flags[room.lockedUntilFlag] !== true;
        rect.setAttribute(
          'class',
          `nb-map-room${room.id === this.playerRoom ? ' here' : ''}${locked ? ' locked' : ''}`,
        );
        svg.append(rect);

        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', String(room.center[0]));
        label.setAttribute('y', String(room.center[2]));
        label.setAttribute('class', 'nb-map-label');
        label.textContent = room.name + (locked ? '（施錠）' : '');
        svg.append(label);
      }

      this.body.append(
        el('div', { class: 'nb-map-wrap' }, [
          el('div', { class: 'nb-map-floor', text: `${floor}F` }),
          svg,
        ]),
      );
    }
    this.body.append(
      el('p', { class: 'nb-empty', text: '現在地は色付きの部屋です。階段は各フロアの東端にあります。' }),
      el('h4', { class: 'nb-h4', text: '部署別IPアドレス' }),
      el('p', { class: 'nb-lead', text: '社内は10.0.0.0/8のプライベートIPを部署別の/18に分けています。マスクは255.255.192.0です。' }),
      ...addressPlan.map(department => el('div', { class: 'nb-card' }, [
        el('div', { class: 'nb-card-title', text: department.label }),
        el('div', { class: 'nb-card-body', text: `${department.cidr} ／ ゲートウェイ ${department.gateway}` }),
      ])),
    );
  }
}
